import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { campaignService } from '../services/api';
import './CheckVoucher.css';

function CheckVoucher() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('scan'); // 'scan' or 'code'
  const [code, setCode] = useState('');
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const scannerRef = useRef(null);
  const scannerInstanceRef = useRef(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (scannerInstanceRef.current) {
      try {
        const state = scannerInstanceRef.current.getState();
        if (state === 2) { // SCANNING
          await scannerInstanceRef.current.stop();
        }
      } catch {}
      scannerInstanceRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setCameraError(null);
    setError(null);
    setVoucher(null);

    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerInstanceRef.current = scanner;
      setScanning(true);

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText) => {
          // QR scanned successfully
          handleQrResult(decodedText);
          stopScanner();
        },
        () => {} // ignore scan failures (no QR found yet)
      );
    } catch (err) {
      setScanning(false);
      if (err.toString().includes('NotAllowedError') || err.toString().includes('Permission')) {
        setCameraError('Akses kamera tidak dibenarkan. Sila benarkan akses kamera dalam tetapan pelayar.');
      } else {
        setCameraError('Gagal membuka kamera. Pastikan peranti anda mempunyai kamera.');
      }
    }
  };

  const handleQrResult = (text) => {
    // Extract UUID from URL like https://kempenku.com/{uuid} or http://localhost:3000/{uuid}
    // UUID pattern: 8-4-4-4-12 hex chars
    const uuidMatch = text.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (uuidMatch) {
      navigate(`/${uuidMatch[1]}`);
    } else {
      setError('QR kod tidak sah. Sila imbas QR kempen yang betul.');
    }
  };

  const handleTabChange = async (newTab) => {
    await stopScanner();
    setTab(newTab);
    setError(null);
    setVoucher(null);
    setCameraError(null);
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!code) return;

    try {
      setLoading(true);
      const response = await campaignService.checkVoucher(code);
      setVoucher(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Voucher tidak dijumpai');
      setVoucher(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="check-voucher animate-fade-in-up">
      <div className="check-card">
        <h1>Semak Voucher</h1>
        <p>Imbas QR kempen atau masukkan kod voucher</p>

        {/* Tab switcher */}
        <div className="check-tabs">
          <button
            className={`check-tab ${tab === 'scan' ? 'active' : ''}`}
            onClick={() => handleTabChange('scan')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Imbas QR
          </button>
          <button
            className={`check-tab ${tab === 'code' ? 'active' : ''}`}
            onClick={() => handleTabChange('code')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 17 10 11 4 5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            Kod Voucher
          </button>
        </div>

        {/* QR Scanner Tab */}
        {tab === 'scan' && (
          <div className="scan-section">
            <div id="qr-reader" ref={scannerRef} className={`qr-reader ${scanning ? 'active' : ''}`} />

            {!scanning && !cameraError && (
              <button className="btn-scan" onClick={startScanner}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Buka Kamera
              </button>
            )}

            {scanning && (
              <button className="btn-scan-stop" onClick={stopScanner}>
                Tutup Kamera
              </button>
            )}

            {cameraError && (
              <div className="check-error">{cameraError}</div>
            )}

            <p className="scan-hint">Halakan kamera ke QR kod kempen untuk mula</p>
          </div>
        )}

        {/* Code Input Tab */}
        {tab === 'code' && (
          <form onSubmit={handleCheck} className="code-form">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Masukkan kod voucher"
            />
            <button type="submit" disabled={loading} className="btn-check">
              {loading ? 'Menyemak...' : 'Semak'}
            </button>
          </form>
        )}

        {error && <div className="check-error">{error}</div>}

        {voucher && (
          <div className="voucher-result animate-fade-in-up">
            <h2>Maklumat Voucher</h2>
            <div className="detail-item">
              <span>Kod</span>
              <strong>{voucher.code}</strong>
            </div>
            <div className="detail-item">
              <span>Status</span>
              <strong className={`status-${voucher.status}`}>
                {voucher.status.charAt(0).toUpperCase() + voucher.status.slice(1)}
              </strong>
            </div>
            <div className="detail-item">
              <span>Kempen</span>
              <strong>{voucher.campaign.name}</strong>
            </div>
            <div className="detail-item">
              <span>Pelanggan</span>
              <strong>{voucher.customer.name}</strong>
            </div>
            {voucher.used_at && (
              <div className="detail-item">
                <span>Tarikh Digunakan</span>
                <strong>{new Date(voucher.used_at).toLocaleString('ms-MY')}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckVoucher;
